import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import { CreditCard } from "lucide-react";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders, handleAuthError } from "../../../utils/authUtils";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import CSVImportGuideModal from "../../../components/CSVImportGuideModal";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
  showUnsavedChangesAlert,
} from "../../uiInterface/base-ui/alertservice";

const importConfigService = {
  createConfiguration: async (
    fieldName: string,
    userId: number,
    tenantId: string
  ) => {
    const response = await fetch(`${API_BASE_URL}/import-configuration/`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fields: fieldName,
        created_by: userId,
        tenant_id: parseInt(tenantId),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create configuration: ${response.statusText}`);
    }

    return await response.json();
  },

  getConfigurations: async (userId: number, tenantId: string) => {
    const response = await fetch(
      `${API_BASE_URL}/import-configuration/?created_by=${userId}&tenant_id=${tenantId}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch configurations: ${response.statusText}`);
    }

    return await response.json();
  },

  deleteConfiguration: async (configId: number) => {
    const response = await fetch(
      `${API_BASE_URL}/import-configuration/${configId}/`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete configuration: ${response.statusText}`);
    }

    return true;
  },
};

const ImportSettings: React.FC = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const { selectedTenantId, auth } = useContext(AuthContext);

  const userRole = useTenantRole(selectedTenantId || 0);
  const canEditRequiredFields = userRole?.role_key !== "SALESUSER";

  const [locallySelectedFields, setLocallySelectedFields] = useState<string[]>(
    []
  );

  const [savedFields, setSavedFields] = useState<string[]>([]);

  const [apiConfigurations, setApiConfigurations] = useState<{
    [key: string]: number;
  }>({});

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  
  const [isGuideModalOpen, setIsGuideModalOpen] = useState<boolean>(false);

  const csvHeaders = [
    "company_name",
    "company_website",
    "company_phone",
    "company_address",
    "company_city",
    "company_state",
    "company_country",
    "company_zip_code",
    "company_currency_code",
    "company_annual_revenue",
    "company_employee_count",
    "company_linkedin_url",
    "company_facebook_url",
    "company_twitter_url",
    "company_assigned_team_name",
    "company_assigned_user_email",
    "contact_salutation",
    "contact_first_name",
    "contact_last_name",
    "contact_primary_email",
    "contact_personal_email",
    "contact_primary_phone",
    "contact_mobile_phone",
    "contact_address_line1",
    "contact_address_line2",
    "contact_city",
    "contact_state",
    "contact_country",
    "contact_zip_code",
    "contact_work_email",
    "contact_work_phone",
    "contact_landline",
    "contact_assigned_user_email",
    "contact_assigned_team_name",
  ];

  const availableFields = csvHeaders.map((header) => {
    return header
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  });

  const transformFieldNameToAPI = (fieldName: string): string => {
    return fieldName.toLowerCase().replace(/\s+/g, "_");
  };

  const transformFieldNameFromAPI = (fieldName: string): string => {
    const transformed = fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return transformed;
  };

  const fetchConfigurationsFromAPI = async () => {
    if (!auth?.user_id || !selectedTenantId) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await importConfigService.getConfigurations(
        auth.user_id,
        selectedTenantId
      );

      const configurations: { [key: string]: number } = {};
      const fields: string[] = [];

      const results = response.results || response;

      if (Array.isArray(results)) {
        results.forEach((config: any) => {
          if (config.fields && config.fields.trim() !== "") {
            const originalFieldName = transformFieldNameFromAPI(config.fields);

            if (availableFields.includes(originalFieldName)) {
              configurations[originalFieldName] =
                config.import_configuration_id;

              if (!fields.includes(originalFieldName)) {
                fields.push(originalFieldName);
              }
            }
          }
        });
      }

      setApiConfigurations(configurations);
      setSavedFields(fields);
      setLocallySelectedFields([]);
    } catch (err: any) {
      console.error("Error fetching configurations:", err);

      if (err.message?.includes("Authentication")) {
        setError("Authentication required. Please login again.");
      } else {
        setError("Failed to load existing configurations");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurationsFromAPI();
  }, [auth?.user_id, selectedTenantId]);

  const allSelectedFields = [...savedFields, ...locallySelectedFields];

  const handleFieldSelect = (selectedOption: any) => {
    const field = selectedOption?.value;
    const allCurrentFields = [...savedFields, ...locallySelectedFields];
    if (field && !allCurrentFields.includes(field)) {
      setLocallySelectedFields([...locallySelectedFields, field]);
      setError("");
    }
  };

  const handleFieldRemove = async (field: string) => {
    if (!auth?.user_id || !selectedTenantId) return;

    const configId = apiConfigurations[field];

    if (!configId) {
      const result = await showConfirmationAlert(
        "Remove Required Field",
        `Remove "${field}" from the unsaved selection?`,
        undefined,
        { confirmButtonText: "Yes, remove it!" }
      );
      if (!result.isConfirmed) return;

      setLocallySelectedFields((prev) => prev.filter((f: string) => f !== field));
      return;
    }

    const result = await showConfirmationAlert(
      "Delete Required Field",
      `Are you sure you want to delete "${field}" from the required fields? It will no longer be required during import.`,
      async () => {
        try {
          await importConfigService.deleteConfiguration(configId);
        } catch (err: any) {
          console.error("Error removing field:", err);
          throw new Error(
            err.message?.includes("Authentication")
              ? "Authentication required. Please login again."
              : `Failed to delete field: ${field}`
          );
        }
      }
    );
    if (!result.isConfirmed) return;

    setError("");
    setSavedFields((prev) => prev.filter((f: string) => f !== field));
    setApiConfigurations((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const saveFields = async (): Promise<boolean> => {
    if (
      !auth?.user_id ||
      !selectedTenantId ||
      locallySelectedFields.length === 0 ||
      isSaving
    )
      return false;

    let saved = false;

    setError("");
    setIsSaving(true);

    const fieldsToSave = [...locallySelectedFields];
    const newApiConfigurations = { ...apiConfigurations };
    const newlySavedFields: string[] = [];

    try {
      for (const field of fieldsToSave) {
        const transformedFieldName = transformFieldNameToAPI(field);
        const response = await importConfigService.createConfiguration(
          transformedFieldName,
          auth.user_id,
          selectedTenantId
        );

        newApiConfigurations[field] = response.import_configuration_id;
        newlySavedFields.push(field);
      }

      saved = true;
      showSuccessAlert(
        fieldsToSave.length === 1
          ? `"${fieldsToSave[0]}" has been saved as a required field.`
          : `${fieldsToSave.length} required fields have been saved.`
      );
    } catch (err: any) {
      console.error("Error saving configuration:", err);

      const message = err.message?.includes("Authentication")
        ? "Authentication required. Please login again."
        : "Failed to save configuration";
      setError(message);
      showErrorAlert(message);
    } finally {
      setApiConfigurations(newApiConfigurations);
      setSavedFields((prev) => [...prev, ...newlySavedFields]);
      setLocallySelectedFields((prev) => prev.filter((f) => !newlySavedFields.includes(f)));
      setIsSaving(false);
    }

    return saved;
  };

  const handleSave = () => {
    saveFields();
  };

  const saveFieldsRef = useRef(saveFields);
  saveFieldsRef.current = saveFields;

  const hasUnsavedChanges = locallySelectedFields.length > 0;

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handleLinkClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest("a");
      if (!anchor || !anchor.href || anchor.hasAttribute("target") || anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;

      e.preventDefault();
      e.stopPropagation();

      const destination = url.pathname + url.search + url.hash;
      showUnsavedChangesAlert().then(async (result) => {
        if (result.isConfirmed) {
          const saved = await saveFieldsRef.current();
          if (saved) navigate(destination);
        } else if (result.isDenied) {
          setLocallySelectedFields([]);
          navigate(destination);
        }
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [hasUnsavedChanges, navigate]);

  const handleDownloadSample = () => {
    setIsDownloading(true);

    const link = document.createElement("a");
    link.href = "/assets/sample-files/sampleCsvFile.csv";
    link.download = "sample-import-file.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setIsDownloading(false);
    }, 1500);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div className="card-body pb-0 pt-2">
            <SettingsTabsNavigation
              activeTab="import"
              variant="bottom"
              includeIcons={true}
              context="organization"
            />
          </div>
        </div>

        {/* Import Settings Content */}
        <div className="row">
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            {/* Settings Sidebar */}
            <div className="card">
              <div className="card-body">
                <div className="settings-sidebar">
                  <h4 className="fw-semibold mb-3">Import</h4>
                  <div className="list-group list-group-flush settings-sidebar">
                    <Link
                      to={route.importSettings}
                      className="fw-medium active"
                    >
                      Import Settings
                    </Link>
                    <Link to={route.importData} className="fw-medium">
                      Import Data
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* /Settings Sidebar */}
          </div>
          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="border-bottom mb-4 pb-3">
                  <h4 className="fw-semibold mb-1">Import Settings</h4>
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="mb-0">
                      Download our sample CSV file to understand the required
                      format
                    </p>
                    <span className="d-flex align-items-center gap-1">
                      <i className="ti ti-info-circle" style={{ color:"blue" }} />
                      <button
                        type="button"
                        onClick={() => setIsGuideModalOpen(true)}
                        style={{ 
                          textDecoration: "underline", 
                          color: "blue",
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer"
                        }}
                      >
                        See the instructions to import data
                      </button>
                    </span>
                  </div>
                  {error && (
                    <div className="alert alert-danger mt-3 mb-0">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  )}
                  {!canEditRequiredFields && (
                    <div className="alert alert-info mt-3 mb-0">
                      <i className="ti ti-info-circle me-2"></i>
                      You have view-only access to required fields settings.
                      Contact your administrator to make changes.
                    </div>
                  )}
                </div>
                {/* Sample CSV Download */}
                <div className="border rounded p-4">
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <h5 className="mb-2">Sample CSV File</h5>
                      <p className="text-muted mb-0">
                        Download our sample CSV file to see the proper format
                        and required columns for data import.
                      </p>
                    </div>
                    <div className="ms-3">
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleDownloadSample}
                        title={
                          isDownloading
                            ? "Downloading..."
                            : "Download Sample CSV"
                        }
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        ) : (
                          <i className="ti ti-file-download"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Required Fields Selection */}
                <div className="mb-4 mt-4">
                  {canEditRequiredFields && (
                    <label className="form-label">Select Required Fields</label>
                  )}
                  {/* Loading indicator */}
                  {isLoading && (
                    <div className="text-center py-3">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">
                        Loading existing configurations...
                      </p>
                    </div>
                  )}

                  {/* Dropdown */}
                  {!isLoading && canEditRequiredFields && (
                    <div className="mb-3">
                      <Select
                        key={allSelectedFields.join(",")}
                        classNamePrefix="react-select"
                        options={availableFields
                          .filter((field) => !allSelectedFields.includes(field))
                          .map((field) => ({ value: field, label: field }))}
                        value={null}
                        onChange={handleFieldSelect}
                        placeholder={"Select fields to require during import"}
                        isClearable
                        isDisabled={isSaving}
                        noOptionsMessage={() => "All fields selected"}
                      />
                    </div>
                  )}

                  {/* Selected Fields Display */}
                  {!isLoading && allSelectedFields.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label mb-2">
                        Selected Required Fields:
                      </label>
                      <div className="d-flex flex-column gap-2">
                        {allSelectedFields.map(
                          (field: string, index: number) => (
                            <div
                              key={index}
                              className="d-flex align-items-center justify-content-between p-3 border rounded bg-white"
                            >
                              <div className="d-flex align-items-center">
                                <span>{field}</span>
                                {apiConfigurations[field] ? (
                                  <i
                                    className="ti ti-check-circle text-success ms-2"
                                    title="Saved to server"
                                  ></i>
                                ) : (
                                  <i
                                    className="ti ti-clock text-warning ms-2"
                                    title="Pending save"
                                  ></i>
                                )}
                              </div>
                              {canEditRequiredFields && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={(e) => {
                                    handleFieldRemove(field);
                                    e.currentTarget.blur();
                                  }}
                                  disabled={!canEditRequiredFields || isSaving}
                                  aria-label={`Remove ${field}`}
                                  title={
                                    !canEditRequiredFields
                                      ? "Contact admin to modify required fields"
                                      : `Remove ${field}`
                                  }
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  {!isLoading && canEditRequiredFields && (
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={locallySelectedFields.length === 0 || isSaving}
                      title={"Contact admin to modify required fields"}
                    >
                      {isSaving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-device-floppy me-2"></i>
                          Save
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CSVImportGuideModal 
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
      />
    </div>
  );
};

export default ImportSettings;
